<template>
  <div v-if="cylinder" class="cylinder-navigation">
    <!-- Header -->
    <div class="nav-header">
      <v-icon small color="#26c6da">mdi-cylinder</v-icon>
      <span class="nav-title">Cylinder Views</span>
      <span class="axis-badge">Axis-Relative</span>
      <v-spacer></v-spacer>
    </div>

    <!-- View Buttons Grid -->
    <div class="view-buttons">
      <v-tooltip bottom v-for="view in cylinderViews" :key="view.name">
        <template v-slot:activator="{ on, attrs }">
          <button
            class="view-btn"
            :class="{ 'active': activeView === view.name }"
            :disabled="!viewer"
            @click="navigateToCylinderView(view.name)"
            v-bind="attrs"
            v-on="on"
          >
            <img :src="view.icon" :alt="view.label" class="view-icon" />
          </button>
        </template>
        <span>{{ view.label }}</span>
      </v-tooltip>
    </div>

    <!-- Corrosion Section -->
    <div class="corrosion-section">
      <v-btn
        block
        small
        depressed
        :color="corrosionEnabled ? 'error' : 'primary'"
        :loading="corrosionLoading"
        @click="toggleCorrosionAnalysis"
        class="corrosion-btn"
      >
        <v-icon small left>{{ corrosionEnabled ? 'mdi-eye-off' : 'mdi-pipe-leak' }}</v-icon>
        {{ corrosionEnabled ? 'Disable Corrosion View' : 'Enable Corrosion View' }}
      </v-btn>

      <v-progress-linear
        v-if="corrosionLoading"
        :value="corrosionProgress"
        color="primary"
        height="3"
        class="mt-2"
      ></v-progress-linear>

      <p v-if="!corrosionEnabled" class="corrosion-hint">
        Analyze pipeline surface for corrosion, wear, and deformation
      </p>

      <!-- Health Indicator (when enabled) -->
      <div v-if="corrosionEnabled && computedCorrosionStatistics" class="health-indicator">
        <v-icon x-small :color="getHealthScoreColor(computedCorrosionStatistics.healthScore)">mdi-heart-pulse</v-icon>
        <span class="health-label">Health:</span>
        <span class="health-value" :style="{ color: getHealthScoreColor(computedCorrosionStatistics.healthScore) }">
          {{ computedCorrosionStatistics.healthScore }}%
        </span>
        <div v-if="computedCorrosionStatistics.lassoFiltered" class="lasso-info">
          <v-icon x-small color="#4a9eff">mdi-lasso</v-icon>
          <span>{{ formatNumber(computedCorrosionStatistics.lassoFilteredCount) }} pts</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { calculateAxisBasedCameraPosition, extractCylinderData } from '@/utils/cylinderCameraUtils'
import { error as debugError } from '@/utils/debugLogger'

const THREE = window.THREE

export default {
  name: 'CylinderAxisNavigation',

  props: {
    cylinder: {
      type: Object,
      default: null
    },
    viewer: {
      type: Object,
      required: true
    },
    potreeViewerComponent: {
      type: Object,
      default: null
    },
    cylinderModeActive: {
      type: Boolean,
      default: false
    },
    corrosionThresholds: {
      type: Object,
      default: null
    },
    corrosionStatistics: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      activeView: null,
      axisVisible: true,
      axisHelper: null,
      animationActive: false,
      corrosionEnabled: false,
      corrosionLoading: false,
      corrosionProgress: 0,
      localCorrosionStatistics: null,
      cylinderViews: [
        { name: 'top', label: 'Top View', icon: '/potree-measurements/libs/potree/resources/icons/top.svg' },
        { name: 'bottom', label: 'Bottom View', icon: '/potree-measurements/libs/potree/resources/icons/bottom.svg' },
        { name: 'front', label: 'Front View', icon: '/potree-measurements/libs/potree/resources/icons/front.svg' },
        { name: 'back', label: 'Back View', icon: '/potree-measurements/libs/potree/resources/icons/back.svg' },
        { name: 'left', label: 'Left View', icon: '/potree-measurements/libs/potree/resources/icons/left.svg' },
        { name: 'right', label: 'Right View', icon: '/potree-measurements/libs/potree/resources/icons/right.svg' }
      ]
    }
  },

  computed: {
    activeClipVolumes() {
      if (!this.viewer || !this.viewer.scene || !this.viewer.scene.volumes) {
        return []
      }
      return this.viewer.scene.volumes.filter(v => v.clip === true)
    },

    currentClipTask() {
      if (!this.viewer) return null
      const ClipTask = window.Potree?.ClipTask
      if (!ClipTask) return null
      if (this.viewer.clipTask === ClipTask.SHOW_INSIDE) return 'SHOW_INSIDE'
      if (this.viewer.clipTask === ClipTask.SHOW_OUTSIDE) return 'SHOW_OUTSIDE'
      if (this.viewer.clipTask === ClipTask.HIGHLIGHT) return 'HIGHLIGHT'
      return null
    },

    computedCorrosionStatistics() {
      return this.corrosionStatistics || this.localCorrosionStatistics;
    },

    isCylinderClipped() {
      if (!this.cylinder || this.activeClipVolumes.length === 0) {
        return false
      }
      const cylinderData = extractCylinderData(this.cylinder)
      if (!cylinderData) return false
      for (const volume of this.activeClipVolumes) {
        if (this.isPointInVolume(cylinderData.position, volume)) {
          return true
        }
      }
      return false
    }
  },

  watch: {
    cylinder: {
      immediate: true,
      handler(newCylinder, oldCylinder) {
        if (newCylinder && newCylinder !== oldCylinder) {
          this.renderAxisHelper()
        } else if (!newCylinder && oldCylinder) {
          this.removeAxisHelper()
        }
      }
    },

    axisVisible(newVal) {
      if (this.axisHelper) {
        this.axisHelper.visible = newVal
      }
    }
  },

  beforeDestroy() {
    this.removeAxisHelper()
  },

  methods: {
    formatNumber(num) {
      if (num === undefined || num === null) return '0'
      return num.toLocaleString()
    },

    navigateToCylinderView(direction) {
      if (!this.cylinder || !this.viewer) return

      const cylinderData = extractCylinderData(this.cylinder)
      if (!cylinderData) return

      const distanceMultiplier = this.calculateOptimalDistanceMultiplier(cylinderData, direction)
      const cameraSetup = calculateAxisBasedCameraPosition(cylinderData, direction, distanceMultiplier)

      if (!cameraSetup) return

      if (this.activeClipVolumes.length > 0) {
        const validationResult = this.validateCameraPositionWithClipVolumes(cameraSetup.position, cameraSetup.lookAt)
        if (!validationResult.valid) {
          this.adjustCameraForClipVolumes(cameraSetup, cylinderData)
        }
      }

      const camera = this.getCamera()
      if (!camera) return

      this.animateCameraTo(camera, cameraSetup.position, cameraSetup.lookAt, cameraSetup.up)
      this.activeView = direction
      this.$emit('view-navigated', { direction, cylinder: this.cylinder, clipVolumes: this.activeClipVolumes.length })
    },

    calculateOptimalDistanceMultiplier(cylinderData, direction) {
      let multiplier = 1.5
      if (this.activeClipVolumes.length === 0) return multiplier
      multiplier = 2.0
      if (direction === 'front' || direction === 'back') multiplier = 2.0
      return multiplier
    },

    validateCameraPositionWithClipVolumes(cameraPosition, lookAtPosition) {
      if (this.currentClipTask === 'SHOW_INSIDE') {
        for (const volume of this.activeClipVolumes) {
          if (this.isPointInVolume(cameraPosition, volume)) {
            return { valid: false, reason: 'Camera inside clip volume' }
          }
        }
      }
      return { valid: true }
    },

    adjustCameraForClipVolumes(cameraSetup, cylinderData) {
      const direction = new THREE.Vector3().subVectors(cameraSetup.position, cameraSetup.lookAt).normalize()
      const extraDistance = direction.length() * 0.5
      cameraSetup.position.add(direction.multiplyScalar(extraDistance))
    },

    isPointInVolume(point, volume) {
      if (!volume || !volume.position || !volume.scale) return false
      try {
        const volumePosition = volume.position
        const volumeScale = volume.scale
        const halfScale = { x: volumeScale.x / 2, y: volumeScale.y / 2, z: volumeScale.z / 2 }
        const inX = Math.abs(point.x - volumePosition.x) <= halfScale.x
        const inY = Math.abs(point.y - volumePosition.y) <= halfScale.y
        const inZ = Math.abs(point.z - volumePosition.z) <= halfScale.z
        return inX && inY && inZ
      } catch (error) {
        return false
      }
    },

    getCamera() {
      if (!this.viewer?.scene) return null
      return this.viewer.scene.getActiveCamera?.() || this.viewer.scene.camera
    },

    animateCameraTo(camera, position, lookAtPoint, upVector, duration = 0) {
      if (!position || !lookAtPoint) return
      const view = this.viewer.scene.view
      const controls = this.viewer.controls
      this.animationActive = true
      view.setView(position, lookAtPoint, duration, () => {
        if (controls && controls.stop) controls.stop()
        if (this.viewer.setNeedsRedraw) this.viewer.setNeedsRedraw()
        this.animationActive = false
      })
    },

    renderAxisHelper() {
      if (!this.cylinder || !this.viewer?.scene?.scene) return
      this.removeAxisHelper()

      const cylinderData = extractCylinderData(this.cylinder)
      if (!cylinderData) return

      const axisLength = cylinderData.height * 1.2
      const axisGeometry = new THREE.BufferGeometry().setFromPoints([
        cylinderData.position.clone().add(cylinderData.axis.clone().normalize().multiplyScalar(-axisLength / 2)),
        cylinderData.position.clone().add(cylinderData.axis.clone().normalize().multiplyScalar(axisLength / 2))
      ])

      const axisMaterial = new THREE.LineBasicMaterial({ color: 0x4a9eff, linewidth: 2 })
      const axisLine = new THREE.Line(axisGeometry, axisMaterial)
      const axesHelper = new THREE.AxisHelper(cylinderData.radius * 2)
      axesHelper.position.copy(cylinderData.position)

      const helperGroup = new THREE.Group()
      helperGroup.add(axisLine)
      helperGroup.add(axesHelper)

      const arrowSize = cylinderData.radius * 0.5
      const axisNorm = cylinderData.axis.clone().normalize()

      const arrowHelper1 = new THREE.ArrowHelper(
        axisNorm,
        cylinderData.position.clone().add(axisNorm.clone().multiplyScalar(cylinderData.height / 2)),
        arrowSize, 0x4a9eff, arrowSize * 0.3, arrowSize * 0.2
      )

      const arrowHelper2 = new THREE.ArrowHelper(
        axisNorm.clone().negate(),
        cylinderData.position.clone().add(axisNorm.clone().multiplyScalar(-cylinderData.height / 2)),
        arrowSize, 0x4a9eff, arrowSize * 0.3, arrowSize * 0.2
      )

      helperGroup.add(arrowHelper1)
      helperGroup.add(arrowHelper2)

      this.axisHelper = helperGroup
      this.viewer.scene.scene.add(helperGroup)
    },

    removeAxisHelper() {
      if (this.axisHelper && this.viewer?.scene?.scene) {
        this.viewer.scene.scene.remove(this.axisHelper)
        this.axisHelper = null
      }
    },

    toggleAxisVisibility() {
      this.axisVisible = !this.axisVisible
    },

    fitCylinderToView() {
      if (!this.cylinder || !this.viewer) return

      const cylinderData = extractCylinderData(this.cylinder)
      if (!cylinderData) return

      const isometricSetup = this.calculateIsometricPosition(cylinderData)
      if (!isometricSetup) return

      const camera = this.getCamera()
      if (!camera) return

      this.animateCameraTo(camera, isometricSetup.position, isometricSetup.lookAt, isometricSetup.up)
      this.activeView = null
      this.$emit('view-fitted', { cylinder: this.cylinder })
    },

    calculateIsometricPosition(cylinderData) {
      const center = new THREE.Vector3(cylinderData.position.x, cylinderData.position.y, cylinderData.position.z)
      const axis = cylinderData.axis.clone().normalize()
      const reference = Math.abs(axis.dot(new THREE.Vector3(1, 0, 0))) > 0.9
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(1, 0, 0)

      const perp1 = new THREE.Vector3().crossVectors(axis, reference).normalize()
      const perp2 = new THREE.Vector3().crossVectors(axis, perp1).normalize()

      const isometricDirection = new THREE.Vector3().add(axis).add(perp1).add(perp2).normalize()
      const multiplier = this.activeClipVolumes.length > 0 ? 2.5 : 1.5
      const maxDimension = Math.max(cylinderData.radius * 2, cylinderData.height)
      const distance = maxDimension * multiplier
      const cameraPosition = center.clone().add(isometricDirection.clone().multiplyScalar(distance))

      return {
        position: cameraPosition,
        lookAt: center,
        up: new THREE.Vector3(0, 0, 1)
      }
    },

    async toggleCorrosionAnalysis() {
      if (this.corrosionEnabled) {
        await this.disableCorrosionAnalysis()
      } else {
        await this.enableCorrosionAnalysis()
      }
    },

    async enableCorrosionAnalysis() {
      if (!this.cylinder || !this.viewer) return

      this.corrosionLoading = true
      this.corrosionProgress = 0

      try {
        const cylinderData = extractCylinderData(this.cylinder)
        if (!cylinderData) throw new Error('Failed to extract cylinder data')

        const potreeViewer = this.potreeViewerComponent
        if (!potreeViewer || !potreeViewer.applyCorrosionVisualization) {
          throw new Error('PotreeViewer component not available')
        }

        const options = {};
        if (this.corrosionThresholds) options.thresholds = this.corrosionThresholds;

        const handleProgress = (progress) => { this.corrosionProgress = progress }
        const handleStats = (stats) => {
          if (!this.corrosionStatistics) this.localCorrosionStatistics = stats
        }

        potreeViewer.$on('corrosion-progress', handleProgress)
        potreeViewer.$on('corrosion-stats-updated', handleStats)

        const statistics = await potreeViewer.applyCorrosionVisualization(cylinderData, options)
        this.corrosionEnabled = true
        if (!this.corrosionStatistics) this.localCorrosionStatistics = statistics

        potreeViewer.$off('corrosion-progress', handleProgress)
        potreeViewer.$off('corrosion-stats-updated', handleStats)

        this.$emit('corrosion-enabled', {
          statistics: this.computedCorrosionStatistics,
          cylinderData,
          usedPreCalculatedData: !!this.corrosionThresholds || !!this.corrosionStatistics
        })

      } catch (error) {
        this.$emit('corrosion-error', error)
      } finally {
        this.corrosionLoading = false
        this.corrosionProgress = 0
      }
    },

    async disableCorrosionAnalysis() {
      try {
        const potreeViewer = this.potreeViewerComponent
        if (potreeViewer && potreeViewer.disableCorrosionVisualization) {
          potreeViewer.disableCorrosionVisualization()
        }
        this.corrosionEnabled = false
        this.localCorrosionStatistics = null
        this.$emit('corrosion-disabled')
      } catch (error) {
        debugError('[CylinderNav] Error disabling corrosion:', error)
      }
    },

    getHealthScoreColor(score) {
      if (score >= 90) return '#4caf50'
      if (score >= 75) return '#8bc34a'
      if (score >= 50) return '#ffc107'
      if (score >= 25) return '#ff9800'
      return '#f44336'
    }
  }
}
</script>

<style scoped>
/* ============================================
   CYLINDER NAVIGATION - Professional CAD Style
   ============================================ */

.cylinder-navigation {
  --bg-panel: #323232;
  --bg-elevated: #3a3a3a;
  --bg-hover: #454545;
  --border-color: #4a4a4a;
  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --text-muted: #707070;
  /* Green accent - Unified with toolbar (#03624c family) */
  --accent-primary: #03624c;
  --accent-primary-light: #a8c97a;
  --accent-blue: #4a9eff;
  --accent-cyan: #26c6da;
  --radius: 4px;
  --transition: 150ms ease;

  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  margin-top: 8px;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
}

/* Header */
.nav-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border-color);
}

.nav-title {
  font-weight: 600;
  color: var(--text-primary);
}

.axis-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  background: rgba(38, 198, 218, 0.15);
  color: var(--accent-cyan);
  border: 1px solid rgba(38, 198, 218, 0.3);
  border-radius: var(--radius);
}

.info-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius);
  transition: all var(--transition);
}

.info-btn:hover {
  background: var(--bg-hover);
  color: var(--text-secondary);
}

/* View Buttons */
.view-buttons {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 14px 12px;
  border-bottom: 1px solid var(--border-color);
  background: rgba(0, 0, 0, 0.1);
}

.view-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  cursor: pointer;
  transition: all var(--transition);
}

.view-btn:hover:not(:disabled) {
  background: var(--bg-hover);
  border-color: var(--accent-primary);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.view-btn.active {
  background: rgba(76, 175, 80, 0.2);
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 1px var(--accent-primary);
}

.view-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.view-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(0.85);
  transition: all var(--transition);
}

.view-btn:hover:not(:disabled) .view-icon {
  filter: brightness(0) saturate(100%) invert(65%) sepia(52%) saturate(600%) hue-rotate(75deg) brightness(95%);
}

.view-btn.active .view-icon {
  filter: brightness(0) saturate(100%) invert(65%) sepia(52%) saturate(600%) hue-rotate(75deg) brightness(110%);
}

/* Corrosion Section */
.corrosion-section {
  padding: 12px;
}

.corrosion-btn {
  font-size: 11px !important;
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
}

.corrosion-hint {
  font-size: 11px;
  color: var(--text-muted);
  text-align: center;
  margin: 8px 0 0;
}

/* Health Indicator */
.health-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 10px;
  background: var(--bg-elevated);
  border-radius: var(--radius);
}

.health-label {
  font-size: 11px;
  color: var(--text-secondary);
}

.health-value {
  font-size: 12px;
  font-weight: 600;
}

.lasso-info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 10px;
  color: var(--text-muted);
}
</style>
